'use client';

import { Button } from '@repo/ui/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@repo/ui/components/ui/dropdown-menu';
import { ArrowLeft, Copy, Edit, FileText, MoreVertical, Printer, TreePine, Trash } from 'lucide-react';

interface DetailsArbreHeaderProps {
  inventaireId: number;
  onEdit: () => void;
  onDuplicate: () => void;
  onPrint: () => void;
  onIntervention: () => void;
  onDelete: () => void;
  isDuplicating?: boolean;
}

export function DetailsArbreHeader({
  inventaireId,
  onEdit,
  onDuplicate,
  onPrint,
  onIntervention,
  onDelete,
  isDuplicating = false,
}: DetailsArbreHeaderProps) {
  const handleBack = () => {
    if (typeof window !== 'undefined') {
      window.history.back();
    }
  };

  return (
    <header className="border-b border-border/60 bg-card dark:border-border/80">
      <div className="container mx-auto px-4 py-3 sm:px-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleBack}
              className="h-8 border-border/60 bg-background px-3 text-xs font-semibold shadow-sm transition-all duration-200 hover:border-border hover:bg-accent/80 hover:shadow-md dark:border-border/80 dark:hover:bg-accent/60"
              aria-label="Retour"
            >
              <ArrowLeft className="mr-1.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="whitespace-nowrap">Retour</span>
            </Button>
            <div
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 ring-1 ring-primary/20 dark:bg-primary/20 dark:ring-primary/30"
              aria-hidden="true"
            >
              <TreePine className="h-4.5 w-4.5 text-primary" />
            </div>
            <div>
              <h1 className="text-base font-semibold leading-tight text-foreground sm:text-lg">
                Détails de l'inventaire
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="default"
              size="sm"
              onClick={onEdit}
              className="h-8 bg-primary px-3 text-xs font-semibold text-primary-foreground shadow-sm transition-all duration-200 hover:bg-primary hover:shadow-md dark:bg-primary dark:hover:bg-primary"
              aria-label="Modifier l'inventaire"
            >
              <Edit className="mr-1.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="whitespace-nowrap">Modifier</span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 border-border/60 bg-background px-3 text-xs font-semibold shadow-sm transition-all duration-200 hover:border-border hover:bg-accent/80 hover:shadow-md dark:border-border/80 dark:hover:bg-accent/60"
                  aria-label="Menu d'actions"
                >
                  <MoreVertical className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  <span className="sr-only">Menu d'actions</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-48 border-border/60 bg-card shadow-lg ring-1 ring-border/20 dark:border-border/80 dark:ring-border/30"
              >
                <DropdownMenuItem
                  onClick={onDuplicate}
                  disabled={isDuplicating}
                  className="cursor-pointer text-xs focus:bg-accent"
                >
                  <Copy className="mr-2 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  {isDuplicating ? 'Duplication...' : 'Dupliquer'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onPrint} className="cursor-pointer text-xs focus:bg-accent">
                  <Printer className="mr-2 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  Imprimer
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onIntervention} className="cursor-pointer text-xs focus:bg-accent">
                  <FileText className="mr-2 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  Fiche d'intervention
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-border/60 dark:bg-border/80" />
                <DropdownMenuItem
                  onClick={onDelete}
                  className="cursor-pointer text-xs text-destructive focus:text-destructive focus:bg-destructive/10"
                >
                  <Trash className="mr-2 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  Supprimer
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
}

